import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Autocomplete,
  Box,
  Typography,
  CircularProgress,
  useTheme,
  alpha,
  Tooltip,
  Alert,
} from '@mui/material';
import { LocationOn, MyLocation, Search, GpsFixed, GpsOff } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { selectLocations } from '../../features/homepage/homepageSlice';

const StyledLocationIcon = styled(LocationOn)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    padding: theme.spacing(1),
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  '& .MuiAutocomplete-input': {
    padding: theme.spacing(1, 0),
  },
}));

// Fallback locations if API data is not available
const fallbackLocations = [
  { title: 'Guwahati', state: 'Assam', country: 'India' },
  { title: 'Mumbai', state: 'Maharashtra', country: 'India' },
  { title: 'Delhi', state: 'Delhi', country: 'India' },
  { title: 'Bangalore', state: 'Karnataka', country: 'India' },
  { title: 'Chennai', state: 'Tamil Nadu', country: 'India' },
  { title: 'Kolkata', state: 'West Bengal', country: 'India' },
  { title: 'Hyderabad', state: 'Telangana', country: 'India' },
  { title: 'Pune', state: 'Maharashtra', country: 'India' },
  { title: 'Ahmedabad', state: 'Gujarat', country: 'India' },
  { title: 'Jaipur', state: 'Rajasthan', country: 'India' },
];

const LocationSearch = ({
  value,
  onChange,
  placeholder = "Enter your location...",
  size = "large",
  fullWidth = true,
  disabled = false,
  error = false,
  helperText = "",
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Get locations from Redux store
  const backendLocations = useSelector(selectLocations);
  
  // Use backend locations if available, otherwise use fallback
  const availableLocations = backendLocations.length > 0 
    ? backendLocations.map(location => ({
        title: location.name,
        state: location.state,
        country: location.country,
        doctor_count: location.doctor_count
      }))
    : fallbackLocations;

  // Filter locations based on input
  const filterLocations = useCallback((inputValue) => {
    if (!inputValue) return availableLocations;
    
    const filtered = availableLocations.filter(location =>
      location.title.toLowerCase().includes(inputValue.toLowerCase()) ||
      location.state.toLowerCase().includes(inputValue.toLowerCase())
    );
    
    return filtered.slice(0, 10); // Limit results for performance
  }, [availableLocations]);

  // Memoized filtered locations for better performance
  const filteredOptions = useMemo(() => {
    if (!value) return availableLocations.slice(0, 10);
    
    const filtered = availableLocations.filter(location =>
      location.title.toLowerCase().includes(value.toLowerCase()) ||
      location.state.toLowerCase().includes(value.toLowerCase())
    );
    
    return filtered.slice(0, 10); // Limit results for performance
  }, [availableLocations, value]);

  useEffect(() => {
    // Simulate API call for locations
    if (!open) {
      setOptions([]);
      return;
    }

    setLoading(true);
    
    // Simulate API delay
    const timer = setTimeout(() => {
      const filteredOptions = filterLocations(value || '');
      setOptions(filteredOptions);
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [open, value, filterLocations]);

  const getCurrentLocation = useCallback(() => {
    setGettingLocation(true);
    setLocationError(null);
    
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser.');
      setGettingLocation(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setLocationError('Location request timed out. Please try again.');
      setGettingLocation(false);
    }, 10000); // 10 second timeout

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        clearTimeout(timeoutId);
        try {
          // Here you would typically make an API call to reverse geocode the coordinates
          // For now, we'll simulate it with a delay
          setTimeout(() => {
            const mockLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              address: 'Current Location',
              title: 'Current Location',
              state: 'Nearby',
              country: 'India',
            };
            
            onChange('Current Location');
            setGettingLocation(false);
          }, 1000);
        } catch (error) {
          console.error('Error getting location:', error);
          setLocationError('Failed to get your location. Please try again.');
          setGettingLocation(false);
        }
      },
      (error) => {
        clearTimeout(timeoutId);
        console.error('Error getting location:', error);
        
        let errorMessage = 'Failed to get your location.';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location services.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred.';
        }
        
        setLocationError(errorMessage);
        setGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000, // Cache for 1 minute
      }
    );
  }, [onChange]);

  // Optimized input change handler
  const handleInputChange = useCallback((event, newInputValue) => {
    setOptions(filteredOptions);
    if (onChange) {
      onChange(newInputValue);
    }
  }, [filteredOptions, onChange]);

  // Optimized option selection handler
  const handleOptionSelect = useCallback((event, newValue) => {
    if (newValue && onChange) {
      onChange(newValue.title || newValue);
    }
  }, [onChange]);

  return (
    <Box sx={{ position: 'relative' }}>
      <StyledAutocomplete
        freeSolo
        options={options}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.title || '';
        }}
        filterOptions={(x) => x} // Disable built-in filtering
        value={value}
        onChange={handleOptionSelect}
        onInputChange={handleInputChange}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        loading={loading}
        disabled={disabled}
        fullWidth={fullWidth}
        size={size}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={error || !!locationError}
            helperText={helperText || locationError}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <StyledLocationIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {gettingLocation ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Tooltip title="Use my current location">
                      <IconButton
                        size="small"
                        onClick={getCurrentLocation}
                        disabled={disabled}
                        sx={{
                          color: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <MyLocation />
                      </IconButton>
                    </Tooltip>
                  )}
                  {loading && <CircularProgress size={20} />}
                </InputAdornment>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {option.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.state}, {option.country}
              </Typography>
            </Box>
          </Box>
        )}
        noOptionsText={
          loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading locations...</Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No locations found
            </Typography>
          )
        }
        sx={{
          '& .MuiAutocomplete-paper': {
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          },
        }}
      />
      
      {locationError && (
        <Alert 
          severity="warning" 
          sx={{ 
            mt: 1, 
            py: 0.5,
            '& .MuiAlert-message': {
              py: 0.5,
            }
          }}
        >
          {locationError}
        </Alert>
      )}
    </Box>
  );
};

export default LocationSearch;
